import template from 'lodash-es/template';
import uiIcon from '@shared/ui-icon';
import paginationTemplate from './templates/paginationTemplate.html';

const defaultOptions = {
  currPage: 1,
  totalItems: 1,
  itemsPerPage: 20,
  onClickCallback: () => {},
};

const PAGE_TYPE = {
  PREV_PAGE: 'prev_page',
  NEXT_PAGE: 'next_page',
  LAST_PAGE: 'last_page',
};

const TOTAL_NUMBERED_LINKS = 3;

/**
 * Generates a unique identifier
 * @return {string}  String for the unique identifier.
 */
function generateUniqueIdentifier() {
  const identifier = `pagination-${Math.floor(Math.random() * 1024)}`;
  // Does the pagination element with that identifier exist? Reroll the
  // dice again to get a new unique one.
  if (document.getElementById(identifier)) {
    return generateUniqueIdentifier();
  }

  return identifier;
}

export default class Pagination {
  /*
   * Pagination Constructor
   * @param {Array}             elementArray            DOM Element id's to
   *                                                    initialise a pagination
   *                                                    object to.
   * @param {Integer}           [options.currPage]      Current Page Number
   * @param {Integer}           [options.totalItems]    Total Items
   * @param {Integer}           [options.itemsPerPage]  Items displayed per page
   * @param {Pagination~onClick}[options.onClick]       Called on click, returns
   *                                                    the page clicked as a
   *                                                    param to the call back
   *                                                    function.
   */
  constructor(containerElementIds = [], opts) {
    // Merge user options with defaultOptions
    const options = Object.assign({}, defaultOptions, opts);
    this.paginationElements = [];
    this.containerElements = [];
    this.currPage = options.currPage;
    this.itemsPerPage = options.itemsPerPage;
    this.totalItems = options.totalItems;
    this.onClickCallback = options.onClickCallback;

    // Find container element, if exists, add them to local variable
    containerElementIds.forEach(id => {
      const currentParentEl = document.getElementById(id);
      if (currentParentEl) {
        this.containerElements.push(currentParentEl);
      }
    });

    this._redrawPaginationElementsAndBindElements();
  }

  /*
   * Sets the current page number and Updates Pagination
   *  @param {Integer} pageNumber The number of items displayed per page
   */
  setCurrPageAndUpdate(pageNumber) {
    if (Number.isNaN(Number.parseInt(pageNumber, 10))) {
      pageNumber = 1;
    }
    pageNumber = Number.parseInt(pageNumber, 10);
    // Handle Invalid Page
    if (pageNumber < 1) {
      pageNumber = 1;
    } else if (pageNumber > this.getTotalPages()) {
      pageNumber = this.getTotalPages();
    }
    this.currPage = pageNumber;
    this._updatePaginationElements();
  }

  /*
   * Changes the items per page and Updates Pagination
   *  PageNumber defaults to be set back to 1
   *  @param {Integer} itemsPerPage The number of items displayed per page
   *  @param {Integer}  pageNumber   The current page displayed
   */
  setItemsPerPageAndUpdate(itemsPerPage, pageNumber = 1) {
    itemsPerPage = Number.parseInt(itemsPerPage, 10);
    if (Number.isNaN(itemsPerPage)) {
      return;
    }
    // Handle Invalid Items Per Page
    if (itemsPerPage < 1) {
      itemsPerPage = defaultOptions.itemsPerPage;
    }
    this.itemsPerPage = itemsPerPage;
    this.setCurrPageAndUpdate(pageNumber);
    this._redrawPaginationElementsAndBindElements();
  }

  /*
   * Changes the total amount of items and Updates Pagination
   *  Updates the whole pagination object, reinject template or alter values
   *  PageNumber defaults to be set back to 1
   *  @param {Integer} totalItems The number of items displayed per page
   *  @param {pageNumber} pageNumber The current page displayed
   */
  setTotalItemsAndUpdate(totalItems, pageNumber = 1) {
    totalItems = Number.parseInt(totalItems, 10);
    if (Number.isNaN(totalItems)) {
      return;
    }
    // Handle Invalid Total Items
    if (totalItems < 0) {
      totalItems = 0;
    }
    this.totalItems = totalItems;
    this._redrawPaginationElementsAndBindElements();
    this.setCurrPageAndUpdate(pageNumber);
  }

  getCurrPage() {
    return this.currPage;
  }

  getItemsPerPage() {
    return this.itemsPerPage;
  }

  getTotalItems() {
    return this.totalItems;
  }

  getTotalPages() {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  /*
   * Given a current page, sets up the page links in the middle of pagination
   * @param {Integer} page page number
   */
  _getPageLinks(page) {
    const pages = [];
    const offset = Math.floor(TOTAL_NUMBERED_LINKS / 2);
    let initial = page - offset;
    let max = page + Math.ceil(TOTAL_NUMBERED_LINKS / 2) - 1;

    const totalPages = this.getTotalPages();
    // If Initial is less than 1, set to 1 and set max to either the total
    //  amount of numbered links shown on pagination or max pages there is
    if (initial < 1) {
      initial = 1;
      max =
        TOTAL_NUMBERED_LINKS > totalPages ? totalPages : TOTAL_NUMBERED_LINKS;
    } else if (max > totalPages) {
      // If Max is more than total pages, set max to total pages and the initial
      //  to an amount that is TOTAL_NUMBERED_LINKS less than the max
      max = totalPages;
      const countdownInitial = totalPages - TOTAL_NUMBERED_LINKS + 1;
      initial = countdownInitial > 0 ? countdownInitial : 1;
    }

    for (let i = initial; i <= max; i++) {
      pages.push(i);
    }
    return pages;
  }

  /*
   * Small Helper function to return next page number
   * @param {Integer} page Given a page number, return next page number
   */
  _getNextPage(page) {
    return page === this.getTotalPages() ? page : page + 1;
  }

  /*
   * Small Helper function to return prev page number
   * @param {Integer} page Given a page number, return previous page number
   */
  _getPrevPageNumber(page) {
    return page === 1 ? 1 : page - 1;
  }

  /*
   * Pagination on Click
   *  This will update the pagination element depending on the click
   */
  _onPaginationClick(e) {
    e.preventDefault();
    const linkData = e.currentTarget.getAttribute('data-link');
    if (!linkData) {
      return;
    }

    // Get the Page Clicked
    let pageClicked = linkData;
    if (linkData === PAGE_TYPE.PREV_PAGE) {
      pageClicked = this._getPrevPageNumber(this.currPage);
    }
    if (linkData === PAGE_TYPE.NEXT_PAGE) {
      pageClicked = this._getNextPage(this.currPage);
    }
    if (linkData === PAGE_TYPE.LAST_PAGE) {
      pageClicked = this.getTotalPages();
    }
    pageClicked = Number.parseInt(pageClicked, 10);

    // If the clicked page is the same as current page, don't call back
    if (pageClicked === this.getCurrPage()) {
      return;
    }

    // Update pagination element/s to curr page
    this.setCurrPageAndUpdate(pageClicked);
    // Callback
    if (typeof this.onClickCallback !== 'function') {
      return;
    }
    this.onClickCallback(pageClicked);
  }

  /*
   *  Will be used to update the pagination elements.
   *   Happens onClick, when totalItems, currPage, itemsPerPage are updated
   */
  _updatePaginationElements() {
    // For each data-link-id from 0 to TOTAL_NUMBERED_LINKS, update it to
    // the new numbers returned by ._getPageLinks
    const pageLinks = this._getPageLinks(this.currPage);
    Array.from(this.paginationElements).forEach(elem => {
      for (let i = 0; i < TOTAL_NUMBERED_LINKS; i++) {
        const dataLink = elem.querySelector(`li[data-link-id='${i}']`);
        if (dataLink) {
          dataLink.setAttribute('data-link', pageLinks[i]);
          const linkEl = dataLink.querySelector('a');
          linkEl.textContent = pageLinks[i];
          if (pageLinks[i] === this.currPage) {
            linkEl.classList.add('is-active');
          } else {
            linkEl.classList.remove('is-active');
          }
        }
      }
    });
  }

  /*
   * Redraw pagination elements
   */
  _redrawPaginationElementsAndBindElements() {
    // Get Links again and Empty Pagination Elements
    this.paginationElements = [];
    Array.from(this.containerElements).forEach(elem => {
      // Generate an Id
      const currPaginationId = generateUniqueIdentifier(elem.id);
      elem.innerHTML = template(paginationTemplate)({
        uiIcon,
        currPaginationId,
        pageLinks: this._getPageLinks(this.currPage),
        currPage: this.currPage,
        prevPage: PAGE_TYPE.PREV_PAGE,
        nextPage: PAGE_TYPE.NEXT_PAGE,
        lastPage: PAGE_TYPE.LAST_PAGE,
      });
      // Add to the list of pagination elemnts held by this instance
      const currPaginationEl = document.getElementById(currPaginationId);
      if (currPaginationEl) {
        this.paginationElements.push(currPaginationEl);
      }
    });

    // Add Event Listener to all Pagination Elements created and their
    // table li elements
    Array.from(this.paginationElements).forEach(elem => {
      Array.from(elem.querySelectorAll('li')).forEach(li => {
        // If no pages, disable the buttons
        if (this.getTotalPages() > 0) {
          li.addEventListener('click', event => this._onPaginationClick(event));
          li.removeAttribute('disabled');
        } else {
          li.addEventListener('click', event => event.preventDefault());
          li.setAttribute('disabled', 'disabled');
        }
      });
    });
  }
}
